import { useEffect, useState } from "react";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { MapPinPlus, Plus, Search, X } from "lucide-react";
import { type Dispatch, type UnknownAction } from "redux";

import {
  administrativeAreasDetailsReset as administrativeAreasDetailsResetAction,
  listAdministrativeAreas as listAdministrativeAreasAction,
  listAdministrativeAreasReset as listAdministrativeAreasResetAction,
  listAdministrativeAreaTypes as listAdministrativeAreaTypesAction,
  listAdministrativeAreaTypesReset as listAdministrativeAreaTypesResetAction,
} from "@/actions";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SortMenu } from "@/components/SortMenu";
import { WFPButton } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { ALL_ITEMS_PAGE_SIZE } from "@/constants/pagination";
import { getCountryObjectFromCache } from "@/utils";

import { ADMINISTRATIVE_AREA_SORT_OPTIONS, DEFAULT_SORTING, List } from "./components/List";
import { ConnectedAdministrativeAreasEditModal } from "./components/Modal";
import {
  type AdministrativeArea,
  type AdministrativeAreasModalOptions,
  type AdministrativeAreaType,
  type EntityId,
} from "./types";

interface AdministrativeAreasListState {
  data: { results?: AdministrativeArea[] };
  errorMessage: string;
  isFetching: boolean;
}

type AdministrativeAreaTypesListResponse =
  | AdministrativeAreaType[]
  | { count?: number; results?: AdministrativeAreaType[] };

interface AdministrativeAreaTypesListState {
  data: AdministrativeAreaTypesListResponse;
}

interface RootState {
  listAdministrativeAreas: AdministrativeAreasListState;
  listAdministrativeAreaTypes: AdministrativeAreaTypesListState;
}

interface StateProps {
  administrativeAreas: AdministrativeArea[];
  administrativeAreaTypes: AdministrativeAreaType[];
  errorMessage: string;
  isFetching: boolean;
}

interface DispatchProps {
  administrativeAreasDetailsReset: () => void;
  listAdministrativeAreas: () => void;
  listAdministrativeAreasReset: () => void;
  listAdministrativeAreaTypes: () => void;
  listAdministrativeAreaTypesReset: () => void;
}

type WfpManagerAdministrativeAreasViewProps = StateProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const CLOSED_MODAL_OPTIONS: AdministrativeAreasModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const getCachedCountryId = () => {
  const country = getCountryObjectFromCache() as { id?: EntityId | null } | null | undefined;

  return country?.id;
};

const getListResults = (data: AdministrativeAreaTypesListResponse): AdministrativeAreaType[] =>
  Array.isArray(data) ? data : (data.results ?? []);

const belongsToCurrentCountry = (areaType: AdministrativeAreaType, countryId?: EntityId | null) => {
  if (countryId === undefined || countryId === null) {
    return true;
  }

  return String(areaType.country) === String(countryId);
};

export const WfpManagerAdministrativeAreasView = ({
  administrativeAreas,
  administrativeAreaTypes,
  errorMessage,
  isFetching,
  administrativeAreasDetailsReset,
  listAdministrativeAreas,
  listAdministrativeAreasReset,
  listAdministrativeAreaTypes,
  listAdministrativeAreaTypesReset,
}: WfpManagerAdministrativeAreasViewProps) => {
  const { t } = useTranslation();
  const countryName = getCountryObjectFromCache().name;
  const [modalOptions, setModalOptions] =
    useState<AdministrativeAreasModalOptions>(CLOSED_MODAL_OPTIONS);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const selectedSort = sorting[0];
  const selectedSortColumn =
    ADMINISTRATIVE_AREA_SORT_OPTIONS.find(sortOption => sortOption.id === selectedSort?.id)?.id ||
    "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  useEffect(() => {
    listAdministrativeAreas();
    listAdministrativeAreaTypes();

    return () => {
      listAdministrativeAreasReset();
      listAdministrativeAreaTypesReset();
    };
  }, [
    listAdministrativeAreas,
    listAdministrativeAreasReset,
    listAdministrativeAreaTypes,
    listAdministrativeAreaTypesReset,
  ]);

  const onClickAdd = () => {
    administrativeAreasDetailsReset();
    setModalOptions({ id: "add", isOpen: true, type: "create" });
  };

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || ADMINISTRATIVE_AREA_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("header.configuration"), href: "/wfpManager/configuration" },
            { label: t("configuration.item.administrativeAreas") },
          ]}
        />
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-y-1">
            <div className="flex items-center gap-x-2">
              <span className="inline-flex shrink-0 items-center justify-center size-10 rounded-lg p-2 bg-blue-100 text-blue-600">
                <MapPinPlus />
              </span>
              <h1 className="font-semibold text-2xl">
                {t("administrativeAreas.pageTitle", { country: countryName })}
              </h1>
            </div>
            <p className="text-muted-foreground">{t("administrativeAreas.subtitle")}</p>
          </div>
          <WFPButton type="button" onClick={onClickAdd}>
            <Plus className="size-4" aria-hidden="true" />
            {t("administrativeAreas.newAdministrativeArea")}
          </WFPButton>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <InputGroup className="w-full max-w-xs sm:w-80">
          <InputGroupAddon>
            <Search className="size-4" aria-hidden="true" />
          </InputGroupAddon>
          <InputGroupInput
            id="administrative-area-search-input"
            type="search"
            value={searchTerm}
            className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            placeholder={t("administrativeAreas.list.searchPlaceholder")}
            aria-label={t("administrativeAreas.list.searchPlaceholder")}
            onChange={event => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t("commodities.clearFilter")}
                onClick={() => setSearchTerm("")}
              >
                <X className="size-3.5" aria-hidden="true" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
        <SortMenu
          options={ADMINISTRATIVE_AREA_SORT_OPTIONS}
          sortColumn={selectedSortColumn}
          sortDirection={selectedSortDirection}
          onSortColumnChange={onSortColumnChange}
          onSortDirectionChange={onSortDirectionChange}
        />
      </div>
      <List
        administrativeAreas={administrativeAreas}
        administrativeAreaTypes={administrativeAreaTypes}
        errorMessage={errorMessage}
        isFetching={isFetching}
        onSortingChange={onSortingChange}
        searchTerm={searchTerm}
        setModalOptions={setModalOptions}
        sorting={sorting}
      />
      <ConnectedAdministrativeAreasEditModal
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
        administrativeAreas={administrativeAreas}
        administrativeAreaTypes={administrativeAreaTypes}
      />
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  const countryId = getCachedCountryId();

  return {
    administrativeAreas: state.listAdministrativeAreas.data.results ?? [],
    administrativeAreaTypes: getListResults(state.listAdministrativeAreaTypes.data).filter(
      areaType => belongsToCurrentCountry(areaType, countryId)
    ),
    isFetching: state.listAdministrativeAreas.isFetching,
    errorMessage: state.listAdministrativeAreas.errorMessage,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listAdministrativeAreas: () =>
      dispatchAction(
        dispatch,
        listAdministrativeAreasAction({
          page_size: ALL_ITEMS_PAGE_SIZE,
          page: 1,
          country: getCachedCountryId(),
        })
      ),
    listAdministrativeAreaTypes: () =>
      dispatchAction(
        dispatch,
        listAdministrativeAreaTypesAction({ page_size: ALL_ITEMS_PAGE_SIZE })
      ),
    listAdministrativeAreasReset: () =>
      dispatchAction(dispatch, listAdministrativeAreasResetAction()),
    listAdministrativeAreaTypesReset: () =>
      dispatchAction(dispatch, listAdministrativeAreaTypesResetAction()),
    administrativeAreasDetailsReset: () =>
      dispatchAction(dispatch, administrativeAreasDetailsResetAction()),
  };
};

export const ConnectedWfpManagerAdministrativeAreasView = connect(
  mapStateToProps,
  mapDispatchToProps
)(WfpManagerAdministrativeAreasView);
